"use client";
import type { ReactNode } from "react";
import { Button, Spinner } from "@cappelendamm/design-system/react";
import { BoxIso, Gift } from "iconoir-react";
import Form from "next/form";
import MastercardIcon from "@/assets/payment-2/Mastercard.svg";
import VisaIcon from "@/assets/payment-2/Visa.svg";
import VippsIcon from "@/assets/payment-2/Vipps.svg";
import {
  type Cart as CartData,
  formatPrice,
} from "@cappelendamm/e-commerce-services";
import classNames from "classnames";
import {
  cartHasPhysicalItems,
  getCartFreightPrice,
  isFreightItem,
} from "../selectors";

const DEFAULT_FREE_SHIPPING_THRESHOLD = Number.MAX_SAFE_INTEGER;

export type CartTotalProps = {
  cart: CartData | null;
  isLoading: boolean;
  vippsInNetsForUnitSale: boolean;
  /** Vipps Hurtigkasse button, shown under "Gå til kassen". */
  vippsExpress?: ReactNode;
  className?: string;
  freeShippingThreshold?: number;
  hasConflictingPaymentConstraints?: boolean;
};

export function CartTotal({
  cart,
  isLoading,
  vippsInNetsForUnitSale,
  vippsExpress,
  className,
  freeShippingThreshold = DEFAULT_FREE_SHIPPING_THRESHOLD,
  hasConflictingPaymentConstraints = false,
}: CartTotalProps) {
  const freight = getCartFreightPrice(cart);
  const showFreight = cart ? cartHasPhysicalItems(cart) : false;
  const productTotal = cart
    ? cart.items
        .filter((item) => !isFreightItem(item))
        .reduce((sum, item) => sum + item.price.gross, 0)
    : 0;
  const effectiveThreshold =
    freeShippingThreshold - (freight?.price ?? 0) - (freight?.discount ?? 0);
  const remainingForFreeShipping = Math.max(
    effectiveThreshold - productTotal,
    0,
  );
  const hasFreeShipping = freight?.price === 0 || remainingForFreeShipping <= 0;
  const isSubmitting = isLoading && Boolean(cart);

  return (
    <Form
      action="/utsjekk"
      className={classNames(
        "col-span-full grid w-full gap-4 text-center",
        className,
      )}
    >
      {showFreight && (
        <div className="grid gap-2 text-left text-base">
          <div className="flex items-center gap-1">
            {hasFreeShipping ? (
              <Gift width={20} height={20} className="shrink-0" />
            ) : (
              <BoxIso width={20} height={20} className="shrink-0" />
            )}
            <div className="relative h-6 flex-1">
              {hasFreeShipping ? (
                <span className="absolute inset-0 flex items-center">
                  Du har nådd gratis frakt!
                </span>
              ) : (
                <span className="absolute inset-0 flex items-center">
                  {`Bare `}
                  <strong className="mx-1">
                    {formatPrice(remainingForFreeShipping)}
                  </strong>
                  {` til gratis frakt!`}
                </span>
              )}
            </div>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            aria-valuenow={Math.min(productTotal, effectiveThreshold)}
            aria-valuemin={0}
            aria-valuemax={effectiveThreshold}
            aria-label="Fremgang mot fri frakt"
          >
            <div
              className={classNames(
                "h-full rounded-full transition-all duration-300",
                hasFreeShipping
                  ? "bg-[#79d279]"
                  : "bg-(--cvr-button-background)",
              )}
              style={{
                width: `${Math.min((productTotal / effectiveThreshold) * 100, 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      <ul className="grid gap-1 text-left text-base">
        {showFreight && (
          <li className="flex justify-between">
            <span>{freight?.price === 0 ? "Gratis frakt" : "Frakt"}</span>
            <span>{formatPrice(freight?.price ?? null)}</span>
          </li>
        )}
        <li className="flex justify-between font-bold">
          <span>Å betale</span>
          <span>{formatPrice(cart?.total.gross ?? null)}</span>
        </li>
      </ul>

      {hasConflictingPaymentConstraints && (
        <p className="max-w-[24rem] rounded border border-amber-200 bg-amber-50 p-3 text-left text-sm text-black">
          Kurven inneholder en forhåndsbestilling (krever faktura) og et
          digitalt produkt (krever kort). Fjern det ene produktet for å fullføre
          kjøpet.
        </p>
      )}
      <Button
        className="w-full"
        type="submit"
        disabled={isLoading || hasConflictingPaymentConstraints}
        autoFocus
      >
        {isSubmitting ? (
          <span className="flex min-h-lh items-center justify-center">
            <Spinner aria-hidden="true" width={21} height={21} />
          </span>
        ) : (
          "Gå til kassen"
        )}
        {isSubmitting && <span className="sr-only">Laster</span>}
      </Button>
      {vippsExpress}
      <div className="flex justify-center gap-2" aria-hidden="true">
        <div className="px-[3.5px]">
          <MastercardIcon alt="Mastercard ikon" height={24} />
        </div>
        <div className="flex items-center">
          <VisaIcon alt="Visa ikon" height={24} />
        </div>
        {vippsInNetsForUnitSale && (
          <div className="flex items-center">
            <VippsIcon alt="Vipps ikon" height={24} />
          </div>
        )}
      </div>
    </Form>
  );
}
